import React, { useEffect, useMemo, useState } from 'react';
import { format } from 'date-fns';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Dna, Search, X, Package, TrendingDown, Globe, Layers, Pencil, Loader2
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const inr = (n) => (n == null || n === '' ? '—' : `₹${Number(n).toLocaleString('en-IN')}`);
const dim = (l, w, h) => {
  if (l == null && w == null && h == null) return '—';
  return `${l ?? '?'} × ${w ?? '?'} × ${h ?? '?'} cm`;
};
const wt = (v, u) => (v == null ? '—' : `${v} ${u || ''}`.trim());

// Inline editable cell — saves on blur or Enter, cancels on Escape.
const EditableCell = ({ value, onSave, type = 'number', placeholder = '—', display, align = 'right', testid }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value ?? '');
  const [saving, setSaving] = useState(false);

  useEffect(() => { setDraft(value ?? ''); }, [value]);

  const commit = async () => {
    const raw = String(draft).trim();
    const newVal = raw === '' ? null : (type === 'number' ? Number(raw) : raw);
    const oldVal = value ?? null;
    if ((newVal ?? null) === (oldVal ?? null)) { setEditing(false); return; }
    setSaving(true);
    try {
      await onSave(newVal);
    } finally {
      setSaving(false);
      setEditing(false);
    }
  };

  if (editing) {
    return (
      <input
        autoFocus
        type={type === 'number' ? 'number' : 'text'}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === 'Enter') e.currentTarget.blur();
          if (e.key === 'Escape') { setDraft(value ?? ''); setEditing(false); }
        }}
        placeholder={placeholder}
        className={`w-20 min-w-[60px] px-1.5 py-0.5 text-sm rounded border border-[#2EB4AC] focus:outline-none focus:ring-2 focus:ring-[#2EB4AC]/30 bg-white ${align === 'right' ? 'text-right' : 'text-left'}`}
        data-testid={testid}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={() => setEditing(true)}
      className={`group inline-flex items-center gap-1 px-1.5 py-0.5 -mx-1.5 rounded hover:bg-[#2EB4AC]/10 transition-colors ${align === 'right' ? 'justify-end' : 'justify-start'}`}
      data-testid={testid ? `${testid}-button` : undefined}
    >
      <span className="truncate">{display ?? (value == null || value === '' ? <span className="text-slate-300">{placeholder}</span> : value)}</span>
      {saving
        ? <Loader2 className="h-3 w-3 animate-spin text-slate-400 shrink-0" />
        : <Pencil className="h-3 w-3 text-slate-300 group-hover:text-[#2EB4AC] opacity-0 group-hover:opacity-100 transition-opacity shrink-0" />}
    </button>
  );
};

export default function SKUDNAPage() {
  const [skus, setSkus] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);

  const [selectedBrand, setSelectedBrand] = useState('all'); // brand_id or 'all'
  const [typeFilter, setTypeFilter] = useState('all');
  const [dnaFilter, setDnaFilter] = useState('all'); // all | full | missing
  const [search, setSearch] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [sRes, bRes] = await Promise.all([
        fetch(`${API_URL}/api/masters/skus`, { credentials: 'include' }),
        fetch(`${API_URL}/api/masters/brands`, { credentials: 'include' }),
      ]);
      if (sRes.ok) setSkus(await sRes.json());
      if (bRes.ok) setBrands(await bRes.json());
    } catch (e) { toast.error('Failed to load SKU DNA'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  // Inline edit saver — patches a single field via PATCH /masters/skus/{id}/dna
  const saveField = async (skuId, field, value) => {
    try {
      const r = await fetch(`${API_URL}/api/masters/skus/${skuId}/dna`, {
        method: 'PATCH', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ [field]: value }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || 'Save failed');
      }
      const updated = await r.json();
      setSkus(prev => prev.map(s => s.id === skuId ? { ...s, ...updated } : s));
      toast.success('Saved');
    } catch (e) {
      toast.error(`Could not save: ${e.message || ''}`);
    }
  };

  // Aggregate count per brand for brand cards
  const brandCounts = useMemo(() => {
    const map = new Map();
    for (const s of skus) {
      const key = s.brand_id || s.brand_name;
      map.set(key, (map.get(key) || 0) + 1);
    }
    return map;
  }, [skus]);

  // Check if a SKU has full DNA data
  const isFullDna = (s) => (
    s.mrp != null && s.bau_price != null && s.deal_price != null && s.exclusive_deal_price != null &&
    !!s.ean && s.length_cm != null && s.width_cm != null && s.height_cm != null && s.weight_value != null
  );

  const filtered = useMemo(() => {
    let r = [...skus];
    if (selectedBrand !== 'all') r = r.filter(s => s.brand_id === selectedBrand);
    if (typeFilter !== 'all') r = r.filter(s => s.product_type === typeFilter);
    if (dnaFilter === 'full') r = r.filter(isFullDna);
    else if (dnaFilter === 'missing') r = r.filter(s => !isFullDna(s));
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(s =>
        (s.product_name || '').toLowerCase().includes(q) ||
        (s.ean || '').toLowerCase().includes(q) ||
        (s.brand_name || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [skus, selectedBrand, typeFilter, dnaFilter, search]);

  // Counts based on current brand + type + search (independent of dna filter for card totals)
  const baseFiltered = useMemo(() => {
    let r = [...skus];
    if (selectedBrand !== 'all') r = r.filter(s => s.brand_id === selectedBrand);
    if (typeFilter !== 'all') r = r.filter(s => s.product_type === typeFilter);
    if (search) {
      const q = search.toLowerCase();
      r = r.filter(s =>
        (s.product_name || '').toLowerCase().includes(q) ||
        (s.ean || '').toLowerCase().includes(q) ||
        (s.brand_name || '').toLowerCase().includes(q)
      );
    }
    return r;
  }, [skus, selectedBrand, typeFilter, search]);

  const fullDnaCount = useMemo(() => baseFiltered.filter(isFullDna).length, [baseFiltered]);
  const missingCount = baseFiltered.length - fullDnaCount;

  const hasActive = selectedBrand !== 'all' || typeFilter !== 'all' || dnaFilter !== 'all' || !!search;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="sku-dna-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 flex items-center justify-center shadow-lg shadow-violet-500/25">
            <Dna className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900">SKUs DNA</h1>
            <p className="text-sm text-slate-500">Complete identity of each SKU — pricing, dimensions, weight, EAN.</p>
          </div>
        </div>
      </div>

      {/* Brand filter pills (inline chips like PDP Checker) */}
      <div>
        <p className="text-xs font-medium text-slate-500 mb-2">Brand</p>
        <div className="flex flex-wrap gap-2">
          <BrandPill
            name="All Brands"
            count={skus.length}
            isActive={selectedBrand === 'all'}
            onClick={() => setSelectedBrand('all')}
            allMode
            testId="brand-pill-all"
          />
          {brands.map(b => (
            <BrandPill
              key={b.id}
              name={b.name}
              iconUrl={b.icon_url}
              count={brandCounts.get(b.id) || 0}
              isActive={selectedBrand === b.id}
              onClick={() => setSelectedBrand(b.id)}
              testId={`brand-pill-${b.id}`}
            />
          ))}
        </div>
      </div>

      {/* Product Type pills */}
      <div>
        <p className="text-xs font-medium text-slate-500 mb-2">Product Type</p>
        <div className="flex flex-wrap gap-2">
          <TypePill label="All Types" active={typeFilter === 'all'} onClick={() => setTypeFilter('all')} allMode testId="type-pill-all" />
          <TypePill label="Single" dot="bg-blue-500" active={typeFilter === 'Single'} onClick={() => setTypeFilter('Single')} testId="type-pill-single" />
          <TypePill label="Combo" dot="bg-amber-500" active={typeFilter === 'Combo'} onClick={() => setTypeFilter('Combo')} testId="type-pill-combo" />
          <TypePill label="Multi-Product" dot="bg-violet-500" active={typeFilter === 'Multi-Product'} onClick={() => setTypeFilter('Multi-Product')} testId="type-pill-multi" />
        </div>
      </div>

      {/* Secondary filters + search */}
      <Card className="border border-slate-200 bg-white shadow-sm">
        <CardContent className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input
              placeholder="Search product name, EAN..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 h-10"
              data-testid="dna-search"
            />
          </div>
          {hasActive && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              <span className="text-[11px] text-slate-500 font-semibold uppercase">Active:</span>
              {selectedBrand !== 'all' && (
                <Chip label={`Brand: ${brands.find(b => b.id === selectedBrand)?.name || '—'}`} onRemove={() => setSelectedBrand('all')} />
              )}
              {typeFilter !== 'all' && <Chip label={`Type: ${typeFilter}`} onRemove={() => setTypeFilter('all')} />}
              {dnaFilter !== 'all' && <Chip label={dnaFilter === 'full' ? 'DNA: Complete' : 'DNA: Missing data'} onRemove={() => setDnaFilter('all')} />}
              {search && <Chip label={`Search: "${search}"`} onRemove={() => setSearch('')} />}
              <button onClick={() => { setSelectedBrand('all'); setTypeFilter('all'); setDnaFilter('all'); setSearch(''); }} className="text-[11px] text-slate-500 hover:text-[#2EB4AC] underline">
                Clear all
              </button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Cards — clickable */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <ClickableStat
          label="Total SKUs"
          value={baseFiltered.length}
          icon={Package}
          color="teal"
          active={dnaFilter === 'all'}
          onClick={() => setDnaFilter('all')}
          testId="stat-total"
        />
        <ClickableStat
          label="With Full DNA"
          value={fullDnaCount}
          icon={Dna}
          color="emerald"
          active={dnaFilter === 'full'}
          onClick={() => setDnaFilter(dnaFilter === 'full' ? 'all' : 'full')}
          testId="stat-full"
        />
        <ClickableStat
          label="Missing Data"
          value={missingCount}
          icon={TrendingDown}
          color="amber"
          active={dnaFilter === 'missing'}
          onClick={() => setDnaFilter(dnaFilter === 'missing' ? 'all' : 'missing')}
          testId="stat-missing"
        />
      </div>

      {/* Table */}
      <Card className="bg-white border border-slate-200 shadow-sm overflow-hidden">
        <CardHeader className="pb-3 border-b border-slate-100">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <CardTitle className="text-base flex items-center gap-2">
              <Dna className="h-4 w-4 text-violet-500" /> SKUs DNA Records
              <Badge variant="outline" className="ml-2 bg-slate-50 text-slate-600 border-slate-200">{filtered.length}</Badge>
            </CardTitle>
            <p className="text-[11px] text-slate-500">Click any value to edit inline · or use Bulk Update SKU DNA from Control Panel.</p>
          </div>
        </CardHeader>
        {loading ? (
          <div className="text-center py-16 text-sm text-slate-500">Loading SKUs DNA...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-16 text-sm text-slate-500">No SKUs for current filters</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-auto min-w-full text-sm">
              <thead>
                <tr className="bg-slate-50/60 border-b border-slate-200">
                  <th className="text-left font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-5 py-3">Product</th>
                  <th className="text-left font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-3 py-3 whitespace-nowrap">Brand</th>
                  <th className="text-right font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-2 py-3 whitespace-nowrap">MRP</th>
                  <th className="text-right font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-2 py-3 whitespace-nowrap">BAU</th>
                  <th className="text-right font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-2 py-3 whitespace-nowrap">Deal</th>
                  <th className="text-right font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-2 py-3 whitespace-nowrap">Excl. Deal</th>
                  <th className="text-left font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-3 py-3 whitespace-nowrap">EAN</th>
                  <th className="text-left font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-3 py-3 whitespace-nowrap">Dimensions</th>
                  <th className="text-left font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-3 py-3 whitespace-nowrap">Weight</th>
                  <th className="text-right font-semibold text-[11px] uppercase tracking-wide text-slate-500 px-5 py-3 whitespace-nowrap">Updated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {filtered.map(s => {
                  const full = isFullDna(s);
                  return (
                    <tr key={s.id} data-testid={`dna-row-${s.id}`} className="hover:bg-slate-50/70 transition-colors">
                      {/* Product */}
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-3">
                          {s.image_url ? (
                            <img src={s.image_url} alt="" className="w-10 h-10 rounded-lg object-cover border border-slate-200 flex-shrink-0" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center flex-shrink-0">
                              <Package className="h-4 w-4 text-slate-400" />
                            </div>
                          )}
                          <div className="min-w-0">
                            <p className="font-medium text-slate-900 whitespace-nowrap" title={s.product_name}>{s.product_name}</p>
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <span className="text-[10px] text-slate-500 bg-slate-100 px-1.5 py-0.5 rounded whitespace-nowrap">{s.product_type}</span>
                              {!full && <span className="text-[10px] font-medium text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded border border-amber-200 whitespace-nowrap">Missing data</span>}
                            </div>
                          </div>
                        </div>
                      </td>

                      {/* Brand */}
                      <td className="px-3 py-3 text-slate-700 text-sm whitespace-nowrap">{s.brand_name}</td>

                      {/* Prices — individual columns, inline-editable */}
                      <td className="px-2 py-3 text-right text-sm font-semibold text-slate-900 whitespace-nowrap">
                        <EditableCell
                          value={s.mrp}
                          display={inr(s.mrp)}
                          onSave={(v) => saveField(s.id, 'mrp', v)}
                          testid={`dna-mrp-${s.id}`}
                        />
                      </td>
                      <td className="px-2 py-3 text-right text-sm text-slate-700 whitespace-nowrap">
                        <EditableCell
                          value={s.bau_price}
                          display={inr(s.bau_price)}
                          onSave={(v) => saveField(s.id, 'bau_price', v)}
                          testid={`dna-bau-${s.id}`}
                        />
                      </td>
                      <td className="px-2 py-3 text-right text-sm text-emerald-700 whitespace-nowrap">
                        <EditableCell
                          value={s.deal_price}
                          display={inr(s.deal_price)}
                          onSave={(v) => saveField(s.id, 'deal_price', v)}
                          testid={`dna-deal-${s.id}`}
                        />
                      </td>
                      <td className="px-2 py-3 text-right text-sm text-violet-700 whitespace-nowrap">
                        <EditableCell
                          value={s.exclusive_deal_price}
                          display={inr(s.exclusive_deal_price)}
                          onSave={(v) => saveField(s.id, 'exclusive_deal_price', v)}
                          testid={`dna-excl-${s.id}`}
                        />
                      </td>

                      {/* EAN — inline-editable */}
                      <td className="px-3 py-3 whitespace-nowrap">
                        <EditableCell
                          value={s.ean}
                          type="text"
                          align="left"
                          display={s.ean
                            ? <span className="font-mono text-[11px] text-slate-700 bg-slate-100 px-2 py-0.5 rounded">{s.ean}</span>
                            : <span className="text-slate-300 text-xs">—</span>}
                          onSave={(v) => saveField(s.id, 'ean', v)}
                          testid={`dna-ean-${s.id}`}
                        />
                      </td>

                      {/* Dimensions — 3 mini editables in a row */}
                      <td className="px-3 py-3 text-slate-600 text-xs whitespace-nowrap">
                        <div className="flex items-center gap-0.5">
                          <EditableCell value={s.length_cm} display={s.length_cm ?? '?'} onSave={(v) => saveField(s.id, 'length_cm', v)} align="left" testid={`dna-len-${s.id}`} />
                          <span className="text-slate-300">×</span>
                          <EditableCell value={s.width_cm} display={s.width_cm ?? '?'} onSave={(v) => saveField(s.id, 'width_cm', v)} align="left" testid={`dna-wid-${s.id}`} />
                          <span className="text-slate-300">×</span>
                          <EditableCell value={s.height_cm} display={s.height_cm ?? '?'} onSave={(v) => saveField(s.id, 'height_cm', v)} align="left" testid={`dna-hei-${s.id}`} />
                          <span className="text-slate-400 ml-0.5">cm</span>
                        </div>
                      </td>

                      {/* Weight — value + unit, both editable */}
                      <td className="px-3 py-3 text-slate-600 text-xs whitespace-nowrap">
                        <div className="flex items-center gap-0.5">
                          <EditableCell value={s.weight_value} display={s.weight_value ?? '—'} onSave={(v) => saveField(s.id, 'weight_value', v)} align="left" testid={`dna-wt-${s.id}`} />
                          <EditableCell value={s.weight_unit} type="text" display={s.weight_unit || 'g'} onSave={(v) => saveField(s.id, 'weight_unit', v)} align="left" testid={`dna-wtu-${s.id}`} />
                        </div>
                      </td>

                      {/* Updated */}
                      <td className="px-5 py-3 text-right text-[11px] text-slate-400 whitespace-nowrap" title={s.dna_updated_at ? format(new Date(s.dna_updated_at), 'PPp') : ''}>
                        {s.dna_updated_at ? format(new Date(s.dna_updated_at), 'dd MMM yy') : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}

const BrandPill = ({ name, iconUrl, count, isActive, onClick, allMode, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
      isActive
        ? 'bg-[#2EB4AC]/10 border-[#2EB4AC] text-[#2EB4AC] shadow-sm'
        : 'bg-white border-slate-200 text-slate-700 hover:border-[#2EB4AC]/50 hover:bg-slate-50'
    }`}
  >
    {allMode ? (
      <Globe className={`h-3.5 w-3.5 ${isActive ? 'text-[#2EB4AC]' : 'text-slate-400'}`} />
    ) : iconUrl ? (
      <img src={iconUrl} alt={name} className="w-4 h-4 rounded object-contain" />
    ) : (
      <div className={`w-4 h-4 rounded flex items-center justify-center text-[9px] font-bold ${isActive ? 'bg-[#2EB4AC]/20 text-[#2EB4AC]' : 'bg-slate-100 text-slate-500'}`}>
        {name.charAt(0)}
      </div>
    )}
    <span className="whitespace-nowrap">{name}</span>
    {count != null && (
      <span className={`text-[10px] px-1.5 py-0 rounded-full font-semibold ${isActive ? 'bg-[#2EB4AC]/15 text-[#2EB4AC]' : 'bg-slate-100 text-slate-500'}`}>{count}</span>
    )}
  </button>
);

const TypePill = ({ label, dot, active, onClick, allMode, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border text-xs font-medium transition-all ${
      active
        ? 'bg-[#2EB4AC]/10 border-[#2EB4AC] text-[#2EB4AC] shadow-sm'
        : 'bg-white border-slate-200 text-slate-700 hover:border-[#2EB4AC]/50 hover:bg-slate-50'
    }`}
  >
    {allMode ? (
      <Layers className={`h-3.5 w-3.5 ${active ? 'text-[#2EB4AC]' : 'text-slate-400'}`} />
    ) : (
      <span className={`w-2 h-2 rounded-full ${dot}`} />
    )}
    <span className="whitespace-nowrap">{label}</span>
  </button>
);

const Chip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#2EB4AC]/10 text-[#2EB4AC] text-[11px] font-medium border border-[#2EB4AC]/20">
    {label}
    <button onClick={onRemove} className="hover:bg-[#2EB4AC]/20 rounded-full p-0.5"><X className="h-2.5 w-2.5" /></button>
  </span>
);

const StatCard = ({ label, value, icon: Icon, color }) => {
  const map = {
    teal: { bg: 'bg-[#2EB4AC]/10', text: 'text-[#2EB4AC]' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600' },
    slate: { bg: 'bg-slate-100', text: 'text-slate-600' },
  }[color] || { bg: 'bg-slate-100', text: 'text-slate-600' };
  return (
    <Card className="border border-slate-200 bg-white">
      <CardContent className="p-4 flex items-center gap-3">
        <div className={`w-9 h-9 rounded-lg ${map.bg} flex items-center justify-center`}>
          <Icon className={`h-4 w-4 ${map.text}`} />
        </div>
        <div>
          <p className="text-xl font-bold text-slate-900 leading-none">{value}</p>
          <p className="text-[11px] text-slate-500 mt-1">{label}</p>
        </div>
      </CardContent>
    </Card>
  );
};

const ClickableStat = ({ label, value, icon: Icon, color, active, onClick, testId }) => {
  const map = {
    teal: { bg: 'bg-[#2EB4AC]/10', text: 'text-[#2EB4AC]', ring: 'ring-[#2EB4AC]', accent: 'bg-[#2EB4AC]' },
    emerald: { bg: 'bg-emerald-100', text: 'text-emerald-600', ring: 'ring-emerald-500', accent: 'bg-emerald-500' },
    amber: { bg: 'bg-amber-100', text: 'text-amber-600', ring: 'ring-amber-500', accent: 'bg-amber-500' },
  }[color] || { bg: 'bg-slate-100', text: 'text-slate-600', ring: 'ring-slate-500', accent: 'bg-slate-500' };
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className={`relative text-left rounded-xl border bg-white px-5 py-4 transition-all hover:shadow-md ${
        active ? `border-transparent ring-2 ${map.ring} shadow-md` : 'border-slate-200 hover:border-slate-300'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className={`w-11 h-11 rounded-xl ${map.bg} flex items-center justify-center flex-shrink-0`}>
          <Icon className={`h-5 w-5 ${map.text}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-3xl font-bold text-slate-900 leading-none tracking-tight">{value}</p>
          <p className="text-xs text-slate-500 mt-1.5 font-medium">{label}</p>
        </div>
        {active && <div className={`absolute top-2 right-2 w-2 h-2 rounded-full ${map.accent}`} />}
      </div>
    </button>
  );
};
